import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Moon, Download } from "lucide-react";
import type { GeneratedStory } from "@/lib/mock-story";
import { toGenitive } from "@/lib/name-utils";
import { downloadStoryPdf } from "@/lib/generate-pdf";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import reviewGood from "@/assets/review_good.webp";
import reviewNormal from "@/assets/review_normal.webp";
import reviewBad from "@/assets/review_bad.webp";
import bgTail from "@/assets/bg_tail.webp";

interface Props {
  storyData: GeneratedStory;
  name: string;
  goal?: string;
  situation?: string;
  onCreateAnother: () => void;
}

/* Shimmer keyframes injected once */
const shimmerStyle = `
@keyframes shimmer-light {
  0%, 100% { opacity: 0; transform: translateX(-30%) skewX(-15deg); }
  50% { opacity: 0.18; transform: translateX(130%) skewX(-15deg); }
}
@keyframes shimmer-dark {
  0%, 100% { opacity: 0; transform: translateX(-30%) skewX(-15deg); }
  50% { opacity: 0.1; transform: translateX(130%) skewX(-15deg); }
}
`;

const ReadingScreen = ({ storyData, name, goal, situation, onCreateAnother }: Props) => {
  const [showParentBlock, setShowParentBlock] = useState(false);
  const [eveningMode, setEveningMode] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rated, setRated] = useState(false);
  const genName = toGenitive(name);

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 120]);

  const handleRate = async (rating: "good" | "normal" | "bad") => {
    setRated(true);
    setShowRating(false);
    await supabase.from("story_ratings").insert({
      rating,
      goal: goal || null,
      situation: situation || null,
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden transition-colors duration-700">
      {/* Inject shimmer keyframes */}
      <style>{shimmerStyle}</style>

      {/* Background image fixed */}
      <div className="fixed inset-0 z-0">
        <img
          src={bgTail}
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
      </div>

      {/* Gradient overlay */}
      <div
        className="fixed inset-0 z-[1] transition-all duration-700"
        style={{
          background: eveningMode
            ? "linear-gradient(to bottom, rgba(10, 15, 25, 0.65), rgba(10, 15, 25, 0.85))"
            : "linear-gradient(to bottom, rgba(248, 245, 240, 0.45), rgba(248, 245, 240, 0.75))",
        }}
      />

      {/* Shimmer effect */}
      <div
        className="fixed inset-0 z-[2] pointer-events-none"
        style={{
          animation: eveningMode
            ? "shimmer-dark 12s ease-in-out infinite"
            : "shimmer-light 10s ease-in-out infinite",
          background: eveningMode
            ? "linear-gradient(105deg, transparent 40%, rgba(200,188,166,0.08) 50%, transparent 60%)"
            : "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Evening mode toggle */}
        <button
          onClick={() => setEveningMode((v) => !v)}
          className="fixed top-4 right-4 z-50 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{
            backgroundColor: eveningMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            color: eveningMode ? "#8a7e6e" : "#9a8e7e",
          }}
          aria-label={eveningMode ? "Выключить вечерний режим" : "Вечерний режим"}
        >
          <Moon size={18} strokeWidth={1.5} />
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="space-y-10"
        >
          {/* Header */}
          <div className="text-center space-y-6">
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-serif transition-colors duration-700"
              style={{
                fontSize: "28px",
                color: eveningMode ? "#d4c8b0" : "#2F2F2F",
              }}
            >
              Для {genName}
            </motion.h1>
            <div
              className="w-12 h-px mx-auto transition-colors duration-700"
              style={{ backgroundColor: eveningMode ? "#3a342a" : "#E6E2DB" }}
            />
          </div>

          {/* Breathing prompt */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.6 }}
            className="font-serif text-center italic transition-colors duration-700"
            style={{
              fontSize: "16px",
              color: eveningMode ? "#8a7e6e" : "#9a8e7e",
              lineHeight: "170%",
            }}
          >
            Сделайте глубокий вдох и устройтесь поудобнее.
          </motion.p>

          {/* Glass card with story text */}
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="font-serif transition-all duration-700 space-y-3 rounded-3xl"
            style={{
              fontSize: eveningMode ? "20px" : "19px",
              lineHeight: eveningMode ? "200%" : "175%",
              color: eveningMode ? "#c8bca6" : "#3a3530",
              backgroundColor: eveningMode
                ? "rgba(26, 24, 20, 0.85)"
                : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              padding: "32px",
            }}
          >
            {storyData.story.split(/\n\n+/).map((paragraph, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 + i * 0.08 }}
              >
                {paragraph}
              </motion.p>
            ))}
          </motion.article>

          {/* Actions */}
          {!eveningMode && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="flex flex-col gap-4 pt-4"
            >
              {/* Parent block */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowParentBlock((v) => !v)}
                className="w-full py-4 rounded-3xl font-sans font-medium text-base text-muted-foreground transition-all hover:opacity-80"
                style={{
                  backgroundColor: "rgba(239, 234, 227, 0.9)",
                  backdropFilter: "blur(12px)",
                }}
              >
                Для родителей
              </motion.button>

              <AnimatePresence>
                {showParentBlock && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div
                      className="rounded-3xl p-7 md:p-8 space-y-8"
                      style={{
                        backgroundColor: "rgba(239, 234, 227, 0.9)",
                        backdropFilter: "blur(12px)",
                      }}
                    >
                      <div className="space-y-3">
                        <h3 className="font-serif text-base text-foreground/70">
                          Для вас
                        </h3>
                        <p className="font-sans text-sm leading-relaxed text-foreground/70">
                          {storyData.parentTip ?? ""}
                        </p>
                      </div>

                      <div className="space-y-3">
                        <h3 className="font-serif text-base text-foreground/70 flex items-center gap-2">
                          <MessageCircle size={16} strokeWidth={1.5} />
                          Поговорите с ребёнком
                        </h3>
                        <ul className="space-y-2.5">
                          {(storyData.discussionQuestions ?? []).map((q, i) => (
                            <li
                              key={i}
                              className="font-sans text-sm leading-relaxed text-foreground/70 pl-4 border-l-2"
                              style={{ borderColor: "#D9D3C7" }}
                            >
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Rating button */}
              {!rated ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowRating(true)}
                  className="w-full py-4 rounded-3xl font-sans font-medium text-base transition-all hover:opacity-80"
                  style={{
                    backgroundColor: "rgba(239, 234, 227, 0.9)",
                    backdropFilter: "blur(12px)",
                    color: "#6b6156",
                  }}
                >
                  Как вам эта история?
                </motion.button>
              ) : (
                <p className="text-center font-sans text-sm" style={{ color: "#9a8e7e" }}>
                  Спасибо за оценку! 💛
                </p>
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => void downloadStoryPdf(storyData, name)}
                className="w-full py-4 rounded-3xl font-sans font-medium text-base flex items-center justify-center gap-2 transition-all hover:opacity-80"
                style={{
                  backgroundColor: "rgba(239, 234, 227, 0.9)",
                  backdropFilter: "blur(12px)",
                  color: "#6b6156",
                }}
              >
                <Download size={18} strokeWidth={1.5} />
                Скачать PDF
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={onCreateAnother}
                className="w-full py-4 rounded-3xl bg-primary text-primary-foreground font-sans font-semibold text-lg transition-shadow hover:shadow-lg"
              >
                Создать ещё
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Rating dialog */}
      <Dialog open={showRating} onOpenChange={setShowRating}>
        <DialogContent className="max-w-sm rounded-3xl border-none p-6" style={{ backgroundColor: "#F8F5F0" }}>
          <DialogHeader>
            <DialogTitle className="font-serif text-center text-lg" style={{ color: "#2F2F2F" }}>
              Как вам эта история?
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center gap-4 pt-2">
            {([
              { key: "bad" as const, img: reviewBad, label: "Не подошло" },
              { key: "normal" as const, img: reviewNormal, label: "Нормально" },
              { key: "good" as const, img: reviewGood, label: "Понравилось" },
            ]).map((item) => (
              <button
                key={item.key}
                onClick={() => handleRate(item.key)}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden transition-transform group-hover:scale-105">
                  <img src={item.img} alt={item.label} className="w-full h-full object-cover" />
                </div>
                <span className="font-sans text-xs" style={{ color: "#6b6156" }}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReadingScreen;
